var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Control from "./Controls/Control";
import * as Controls from "./Controls/index";
import ValidationMessages from "./Data/ValidationMessages";
import { FieldType } from "./Enum/FieldType";
import { ModalType } from "./Enum/ModalType";
import { Utils } from "./Utils/Utils";
/**
 * This class represents a dynamic form that can be rendered from a JSON data source.
 * It allows the user to fill in the form fields and submit or save the progress of the form.
 * It also provides validation and error handling for the form fields.
 */
export class SABISDynamicForm {
    /**
     * Creates a new instance of SABISDynamicForm with the given options.
     * @param options The options for the dynamic form.
     */
    constructor(options) {
        this.fields = [];
        this.isSubmitting = false;
        this.countryCodes = [];
        this.options = options;
        this.options.showSubmitButton =
            this.options.showSubmitButton && !this.options.previewMode;
    }
    /**
     * Loads the form data from the api and sets the initial values for the form fields.
     */
    loadFormData() {
        return __awaiter(this, void 0, void 0, function* () {
            this.clearFormData();
            yield Utils.apiCall(this.options.apiUrl + "/" + this.options.formFieldsUrl, "POST", this.options.reuseApplicationData)
                .then((data) => {
                this.loadFormDataCallBack(data);
            })
                .catch((error) => {
                console.error("Fail to get form data:", error);
            });
        });
    }
    /**
     * toggle all fields as disabled or not
     * @param isDisabled
     */
    toggleDisabled(isDisabled) {
        const fieldSets = document.querySelectorAll("form>fieldset");
        if (!isDisabled) {
            fieldSets.forEach((el) => {
                el.classList.add("enabled");
            });
        }
        else {
            fieldSets.forEach((el) => {
                el.classList.remove("enabled");
            });
        }
    }
    /**
     * Loads the form and sets the initial values for the form fields.
     * @param formData
     */
    loadForm(formData) {
        this.clearFormData();
        this.loadFormDataCallBack(formData);
    }
    /**
     * Open a preview window of the form and initiate print
     * @param printStyles styles to be added to print
     */
    print(printStyles = "", title = "") {
        const originalPreviewMode = this.options.previewMode;
        this.options.previewMode = true;
        const printWindow = window.open("", "", "width=800,height=900,toolbar=0,scrollbars=0,status=0");
        if (printWindow) {
            if (printStyles) {
                printWindow.document.write(`<link rel="stylesheet" href="${printStyles}" />`);
            }
            printWindow.document.write(`
        <style>
        input[type=file]{display:none!important;}
        fielset{border:none!important;}
        </style>
        <div class="sabis-dynamicform print">
          ${title ? `<h1>${title}</h1>` : ``}  
        <div class="apply-container">
            <article>
              ${this.getHtml().join("")}
            </article>
          </div>
        </div>`);
            printWindow.document.close();
            printWindow.focus();
            printWindow.addEventListener("load", () => {
                setTimeout(() => {
                    //give time for styles to load
                    printWindow.print();
                }, 2000);
            });
        }
        this.options.previewMode = originalPreviewMode;
    }
    /** remove fields and clear values */
    clearFormData() {
        this.formFields = null;
        this.values = null;
        this.fields = [];
    }
    /**
     * Handles the response from the api and renders the form, groups and save progress elements.
     * @param data The data returned from the api.
     */
    loadFormDataCallBack(data) {
        this.formFields = data;
        if (this.options.additionalGroups) {
            this.formFields.editorData.groups.unshift(...this.options.additionalGroups);
        }
        this.formFields.editorData.groups.sort((a, b) => a.order - b.order);
        this.formId = `sabis-dynamic-form-${this.formFields.versionId}`;
        this.countryCodes = data.editorData.countryCodes;
        if (this.formFields.editorFieldsValues &&
            this.formFields.editorFieldsValues.fieldsValues) {
            this.values = this.initialValues();
        }
        this.renderForm();
        this.renderGroups();
        this.renderSaveProgress();
        if (this.options.afterRender) {
            this.options.afterRender(this);
        }
        this.checkForValidGroups();
    }
    /**
     * Returns the initial values of the form coming from the api call.
     */
    initialValues() {
        return this.formFields.editorFieldsValues.fieldsValues;
    }
    checkForValidGroups() {
        var _a;
        (_a = this.formFields) === null || _a === void 0 ? void 0 : _a.editorData.groups.forEach((g) => this.validateGroup(g));
    }
    /**
     * Validates all the form fields and shows a modal with validation messages if there are any errors.
     * @returns A boolean value indicating whether the validation passed or not.
     */
    validate(hideValidationMessage) {
        let validationMessages = [];
        this.fields.forEach((field) => {
            field.control.validate();
            if (field.validationMessages && field.validationMessages.length > 0)
                validationMessages.push(...field.validationMessages);
        });
        if (validationMessages.length > 0 && !hideValidationMessage) {
            Utils.modal(ModalType.Validation, ValidationMessages.pleaseFix(), [...["<ul>"], ...validationMessages, ...["</ul"]].join("</li><li>"));
        }
        return validationMessages.length === 0;
    }
    submitButton() {
        return document.querySelector(`form[id="${this.formId}"] button[type="submit"]`);
    }
    beforeSubmit() {
        if (!this.isSubmitting) {
            this.submitButton().classList.toggle("submitting");
            this.submitButton().innerHTML = "Please wait";
            this.isSubmitting = true;
        }
        return true;
    }
    afterSubmit() {
        this.isSubmitting = false;
        this.submitButton().classList.toggle("submitting");
        this.submitButton().innerHTML = this.options.submitText;
    }
    /**
     * Submits the form data to the api and redirects to the success url if the submission is successful.
     * @param ev The event object of the submit event.
     */
    submitForm(ev) {
        ev.preventDefault();
        const form = SABISDynamicForm.getForm(ev);
        if (form.isSubmitting)
            return;
        form.beforeSubmit();
        if (form.validate() && (Utils.isOk(form.options.onBeforeSubmit) && form.options.onBeforeSubmit(form))) {
            Utils.apiCall(form.options.apiUrl + "/" + form.options.formActionUrl, "POST", form.formResult())
                .then((data) => {
                //TODO: handle errors
                // form.afterSubmit();
                top.location.href = form.options.successUrl;
            })
                .catch((error) => {
                form.afterSubmit();
                console.error("Fail to submit application:", error);
            });
        }
        else {
            form.afterSubmit();
        }
    }
    /**
     * Saves the progress of the form to the api and calls the save progress feedback event if provided.
     * @param ev The event object of the click event.
     */
    saveProgress(ev, formId = null, forceIt = false, email = "") {
        if (ev)
            ev.preventDefault();
        if (!formId) {
            formId = ev.currentTarget.attributes["data-form-id"].value;
        }
        const form = top.window[formId];
        if (forceIt ||
            (form.options.saveProgressValidation &&
                form.options.saveProgressValidation(formId))) {
            Utils.apiCall(form.options.apiUrl + "/" + form.options.saveProgressUrl, "POST", Object.assign({ emailAddress: email }, form.formResult()))
                .then((data) => {
                form.options.saveProgressFeedback(true);
            })
                .catch((error) => {
                console.error("Fail to save progress:", error);
            });
        }
    }
    /**
     * Returns the form result as an object with the field names and values.
     */
    formResult() {
        let values = this.getValues();
        let formResult = {
            formTemplateVersionId: this.formFields.versionId,
            formFields: values.fields,
        };
        return formResult;
    }
    /**
     * Renders the group links in the specified container element.
     */
    renderGroup(group, groupType = "group") {
        var _a;
        group.nodes = group.fields;
        if (group.subGroups)
            group.nodes = group.fields.concat(group.subGroups);
        group.nodes.sort((a, b) => a.order - b.order);
        let html = [];
        if (groupType === "group") {
            group.isSubGroup = false;
            html.push(`<fieldset id="group-${group.order}"><legend>${group.title}</legend>`);
        }
        else {
            group.isSubGroup = true;
            html.push(`<div id="sub-group-${group.order}"><h4>${group.title}</h4>`);
        }
        (_a = group.nodes) === null || _a === void 0 ? void 0 : _a.forEach((node) => {
            if (node.fieldType) {
                const field = node;
                this.initFieldControl(field);
                field.group = group;
                html.push(field.control.render());
                this.fields.push(field);
            }
            else {
                html.push(...this.renderGroup(node, "sub-group"));
            }
        });
        html.push(groupType === "group" ? "</fieldset>" : "</div>");
        return html;
    }
    /**
     * Returns the html container of the group
     * @param group The group to validate
     */
    getGroupContainer(group) {
        return document.getElementById(`group-${group.order}`);
    }
    /**
     * Returns the html container of the sub group
     * @param group The sub group to validate
     */
    getSubGroupContainer(subGroup) {
        return document.getElementById(`sub-group-${subGroup.order}`);
    }
    /**
     * Validate all groups
     */
    validateGroups() {
        var _a;
        (_a = this.formFields) === null || _a === void 0 ? void 0 : _a.editorData.groups.map((g) => {
            this.validateGroup(g);
        });
    }
    /**
     * Validate a group and add a valid class if a group is validated
     * @param group The group to validate
     */
    validateGroup(g) {
        const groupContainer = g.isSubGroup ? this.getSubGroupContainer(g) : this.getGroupContainer(g);
        const isValidGroup = this.isGroupValid(g);
        if (isValidGroup.isValid && isValidGroup.hasValue) {
            groupContainer.classList.add(`valid`);
        }
        else {
            groupContainer.classList.remove(`valid`);
        }
    }
    /**
     * Checks if a group is valid and that it has a value
     * @param group The group to validate
     */
    isGroupValid(group) {
        let isValid = true;
        let hasValue = false;
        for (let i = 0; i < group.fields.length; i++) {
            let field = group.fields[i];
            hasValue || (hasValue = field.control.hasValue());
            if (!field.control.validate(false, true))
                //no need to continue validation if one field is invalid
                return { isValid: false, hasValue: hasValue };
        }
        return { isValid: isValid, hasValue: hasValue };
    }
    /**
     * Renders the form fields in the specified container element.
     */
    renderForm() {
        top.window[this.formId] = this;
        if (this.options.formFieldsContainer !== undefined) {
            const container = document.querySelector(this.options.formFieldsContainer);
            if (container !== null) {
                container.innerHTML = this.getHtml().join("");
                if (this.options.showSubmitButton) {
                    this.submitButton().addEventListener("click", this.submitForm);
                }
            }
            if (!this.options.previewMode) {
                let inputFields = document.querySelectorAll(`form[id="${this.formId}"] input, form[id="${this.formId}"] select`);
                inputFields.forEach((field) => {
                    field.addEventListener("focus", this.onFieldFocus);
                    field.addEventListener("blur", this.onFieldBlur);
                    field.addEventListener("change", this.onFieldChange);
                });
            }
            this.fields.forEach((field) => {
                field.control.afterRender();
            });
        }
    }
    /**
     * Returns the html of the form.
     */
    getHtml() {
        var _a;
        const groups = (_a = this.formFields) === null || _a === void 0 ? void 0 : _a.editorData.groups;
        let html = [
            `<form id="${this.formId}"${this.options.previewMode ? "class=preview" : ""}>`,
        ];
        groups === null || groups === void 0 ? void 0 : groups.forEach((group) => {
            html.push(...this.renderGroup(group));
        });
        if (this.options.showSubmitButton) {
            // add submit button
            html.push(`<button type="submit">${this.options.submitText} </form>`);
        }
        return html;
    }
    /**
     * Renders the save progress button in the specified container element.
     */
    renderSaveProgress() {
        if (this.options.saveProgressContainer &&
            this.options.saveProgressUrl &&
            !this.options.previewMode) {
            const container = document.querySelector(this.options.saveProgressContainer);
            container.innerHTML = "";
            let button = document.createElement("button");
            button.innerHTML = this.options.saveProgressHtml;
            button.setAttribute("data-form-id", this.formId);
            button.onclick = this.saveProgress;
            container.appendChild(button);
        }
    }
    /**
     * Renders the group links in the specified container element.
     */
    renderGroups() {
        var _a;
        if (this.options.formGroupsContainer !== undefined) {
            const container = document.querySelector(this.options.formGroupsContainer);
            if (container !== null && this.formFields !== null) {
                const groups = (_a = this.formFields) === null || _a === void 0 ? void 0 : _a.editorData.groups;
                let html = ["<ul>"];
                groups === null || groups === void 0 ? void 0 : groups.forEach((group) => {
                    html.push(`<li><a href="#group-${group.order}">${group.title}</a></li>`);
                });
                html.push("</ul>");
                container.innerHTML = html.join("");
            }
        }
    }
    /**
     * Initializes the field control based on the field type and options.
     * @param field The field object to initialize the control for.
     */
    initFieldControl(field) {
        field.form = this;
        switch (field.fieldType) {
            case FieldType.Label:
                field.control = new Controls.LabelControl(field);
                break;
            case FieldType.Int:
            case FieldType.Decimal:
                field.control = new Controls.InputNumberControl(field);
                break;
            case FieldType.Selection:
                field.control = new Controls.SelectControl(field);
                break;
            case FieldType.DateOnly:
            case FieldType.DateTime:
                field.control = new Controls.DateControl(field);
                break;
            case FieldType.File:
                field.control = new Controls.InputFileControl(field);
                break;
            case FieldType.Email:
                field.control = new Controls.InputEmailControl(field);
                break;
            case FieldType.PhoneNumber:
                field.control = new Controls.InputPhoneNumberControl(field, this.countryCodes);
                break;
            default:
                field.control = new Controls.InputTextControl(field);
                break;
        }
    }
    /**
     * Returns the values of the form fields as an array of objects with the field name and value.
     */
    getValues() {
        var _a;
        let ret = {
            fields: {},
        };
        (_a = this.fields) === null || _a === void 0 ? void 0 : _a.forEach((field) => {
            if (field.excludeValue)
                return;
            let value = field.control.getValue();
            if (Utils.isOk(value) && value !== "")
                ret.fields[field.internalId] = value;
        });
        return ret;
    }
    /**
     * Returns the field object with the given name from the fields array.
     * @param name The name of the field to get.
     */
    getField(ev) {
        const formControl = Control.getFormControl(ev);
        return formControl
            ? this.fields.find((f) => f.internalId === formControl.id)
            : null;
    }
    /**
     * Handles the focus event for a field and sets the active class for the field element.
     * @param ev The event object of the focus event.
     */
    onFieldFocus(ev) {
        const form = SABISDynamicForm.getForm(ev);
        const field = form.getField(ev);
        field.control.onFocus();
    }
    /**
     * Handles the blur event for a field and removes the active class from the field element.
     * @param ev The event object of the blur event.
     */
    onFieldBlur(ev) {
        const form = SABISDynamicForm.getForm(ev);
        const field = form.getField(ev);
        field.control.onBlur();
    }
    /**
     * Handles the change event for a field and updates the value of the field in the values object.
     * @param ev The event object of the change event.
     */
    onFieldChange(ev) {
        const form = SABISDynamicForm.getForm(ev);
        const field = form.getField(ev);
        //For file control onChange is called internally after upload complete
        //Otherwise, it will trigger validation before upload
        if (!(field.control instanceof Controls.InputFileControl))
            field.control.onChange();
    }
    /* static functions */
    /**
     * Returns the SABISDynamicForm instance associated with a given event target element.
     * @param ev The event object that contains the target element.
     */
    static getForm(ev) {
        const formId = Utils.closest(ev.currentTarget, "form").id;
        return top.window[formId];
    }
}
//Autotest:
//function fillElement(el) { el.value = el.parentNode.parentNode.childNodes[0].innerHTML.split(':')[0] + ' ' + Math.random() }
//$('input[required]').each(function(){fillElement(this)});
//$('select').each(function(){this.selectedIndex = 1;});
